/**
 * Reservation domain models for the dashboard.
 *
 * Mirrors the data model in fnf-docs → Backend → Reservation Platform, in
 * camelCase; `services/reservation-api.mapper.ts` converts to and from the
 * snake_case wire shapes of `/v1/restaurants/reservations`.
 */

import { LEGAL_URLS } from './legal';

export type ReservationMode = 'off' | 'timed' | 'waitlist' | 'request' | 'hybrid';

export type ReservationKind = 'reservation' | 'waitlist';

export type ReservationType = 'timed' | 'request' | 'waitlist' | 'ticketed';

export type ReservationStatus =
  | 'pending'
  | 'confirmed'
  | 'declined'
  | 'cancelled'
  | 'seated'
  | 'completed'
  | 'no_show'
  | 'waitlisted'
  | 'notified'
  | 'expired';

export type ReservationChannel =
  'fynkle_app' | 'dashboard' | 'google' | 'opentable' | 'ai_chat' | 'ai_phone' | 'web';

/**
 * What the restaurant keeps about a guest between bookings (FNF-442), apart
 * from one booking's `notes`. Allergies are health information: shown to
 * staff, never logged.
 */
export interface GuestStandingNotes {
  allergies: string | null;
  preferences: string | null;
  occasions: string | null;
  tags: string[];
}

/** A change to the standing notes: a field left out is kept, '' or [] clears it. */
export type StandingNotesChange = Partial<GuestStandingNotes>;

export interface ReservationGuest {
  userId?: string | null;
  name: string;
  phone?: string | null;
  email?: string | null;
  /**
   * Seated or completed bookings at this restaurant. Counts this booking too
   * once it is seated, so the badge uses `priorVisits()`.
   */
  visits?: number;
  // The guest's history here, from reservation-service for staff (FNF-442).
  noShows?: number | null;
  lastVisitAt?: string | null;
  /** "Missed {recentNoShows} of the last {recentBookings} bookings". */
  recentBookings?: number | null;
  recentNoShows?: number | null;
  /** Reservations made here (walk-ins excluded), and the latest one's start. */
  bookings?: number | null;
  lastBookingAt?: string | null;
  /** Null when the guest has no phone, email or account to be recognised by. */
  standingNotes?: GuestStandingNotes | null;
  guestKey?: string | null;
}

/**
 * A guest found by phone, email or account before a booking is made
 * (`GET …/reservations/guests/lookup`), so New reservation can greet a
 * returning guest and show their allergies. Never carries contact details.
 */
export interface GuestLookup {
  known: boolean;
  visits: number;
  noShows: number;
  lastVisitAt: string | null;
  bookings: number;
  lastBookingAt: string | null;
  standingNotes: GuestStandingNotes;
  guestKey: string;
  /**
   * The guest's open reservations (pending, confirmed, seated) not yet over,
   * earliest first; one list for the phone and the email together.
   */
  existing: ExistingReservation[];
}

export interface ExistingReservation {
  reservationId: string;
  startAt: string;
  endAt: string;
  serviceDate: string;
  partySize: number;
  status: ReservationStatus;
  tableNames: string[];
  guestName: string;
}

export interface ReservationRisk {
  /** 0..1 probability of a no-show from the AI model. */
  noShowScore: number;
  modelVersion: string;
}

export interface Reservation {
  reservationId: string;
  restaurantId: string;
  kind: ReservationKind;
  type: ReservationType;
  status: ReservationStatus;
  /** Why it is declined / cancelled / expired, when the caller said. */
  statusReason?: string | null;
  channel: ReservationChannel;
  provider: string;
  /** ISO 8601 UTC. */
  startAt: string;
  endAt: string;
  /** YYYY-MM-DD in the restaurant's time zone. */
  serviceDate: string;
  partySize: number;
  guest: ReservationGuest;
  tableIds: string[];
  tableNames: string[];
  zoneId?: string | null;
  notes?: string | null;
  occasion?: string | null;
  risk?: ReservationRisk | null;
  version: number;
  createdAt: string;
  modifiedAt: string;
  // Waitlist-only fields
  position?: number;
  quotedWaitMinutes?: number;
  notifiedAt?: string | null;
  /**
   * Waitlist: when a notified party's hold runs out. A pending request
   * (FNF-308): the deadline to answer it, after which it is declined.
   */
  expiresAt?: string | null;
  /**
   * Only on a waitlist "table ready" response: whether the guest was texted.
   * sent | no_phone | disabled | invalid_phone | opted_out | failed | duplicate | unavailable
   */
  smsStatus?: string | null;
  /**
   * On a booking change the guest was told about (FNF-1130): how the email went.
   * sent | skipped | disabled | invalid | failed | duplicate | unavailable
   */
  emailStatus?: string | null;
}

export interface AvailabilitySlot {
  startAt: string;
  /** "7:30 PM" for the picker. */
  label: string;
  available: boolean;
  tableIds: string[];
  tableNames: string[];
  /** Why it is unavailable, when it is. */
  reason?: string;
}

export interface TurnTimeBand {
  maxParty: number;
  minutes: number;
}

export interface ReservationSettings {
  mode: ReservationMode;
  slotIntervalMinutes: number;
  turnTimes: TurnTimeBand[];
  pacing: { maxCoversPerInterval: number };
  party: { min: number; max: number; autoConfirmUpTo: number };
  window: { minNoticeMinutes: number; maxDaysAhead: number };
  policy: { depositCents: number; cancelCutoffMinutes: number; noShowGraceMinutes: number };
  requestExpiryMinutes: number;
  waitlist: { quoteStrategy: 'simple' | 'learned'; notifyTimeoutMinutes: number };
  channels: {
    google: { enabled: boolean };
    opentable: { enabled: boolean; merchantId: string | null };
  };
  notifications: { sms: boolean; email: boolean; push: boolean; reminderMinutesBefore: number[] };
  version: number;
}

/** A seatable table as the reservation views need it (from `/v1/restaurants/tables`). */
export interface ReservationTable {
  id: string;
  name: string;
  seats: number;
  zoneId: string;
}

export interface ServicePeriod {
  name: string;
  /** "HH:mm" local. */
  start: string;
  end: string;
}

/**
 * Whether the restaurant serves on a date: `unset` when no serving hours are
 * configured at all, `closed` when hours exist but none cover that weekday,
 * `closed_date` when that date is closed on its own (a holiday, FNF-1180).
 * In each case the availability engine has no window to build slots in.
 */
export type ServingHoursState = 'open' | 'closed' | 'closed_date' | 'unset';

export interface CreateReservationInput {
  startAt: string;
  partySize: number;
  guest: ReservationGuest;
  notes?: string | null;
  occasion?: string | null;
  channel?: ReservationChannel;
  /** Hosts may confirm directly; diners in request mode land as pending. */
  status?: 'pending' | 'confirmed';
  tableIds?: string[];
  /** Saved on the guest, so the next booking carries them too. */
  standingNotes?: StandingNotesChange | null;
}

export interface JoinWaitlistInput {
  partySize: number;
  guest: ReservationGuest;
  notes?: string | null;
  standingNotes?: StandingNotesChange | null;
}

export const RESERVATION_STATUS_LABELS: Record<ReservationStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  declined: 'Declined',
  cancelled: 'Cancelled',
  seated: 'Seated',
  completed: 'Completed',
  no_show: 'No-show',
  waitlisted: 'Waiting',
  notified: 'Notified',
  expired: 'Expired',
};

export const CHANNEL_LABELS: Record<ReservationChannel, string> = {
  fynkle_app: 'Fynkle app',
  dashboard: 'Dashboard',
  google: 'Google',
  opentable: 'OpenTable',
  ai_chat: 'AI chat',
  ai_phone: 'AI phone',
  web: 'Web',
};

/** Boxicons class per channel, for the book's "Via" column and the request queue. */
export const CHANNEL_ICONS: Record<ReservationChannel, string> = {
  fynkle_app: 'bx-mobile-alt',
  dashboard: 'bx-desktop',
  google: 'bxl-google',
  opentable: 'bx-link-external',
  ai_chat: 'bx-bot',
  ai_phone: 'bx-phone-call',
  web: 'bx-globe',
};

/** Statuses that no longer hold capacity. */
export const CLOSED_STATUSES: ReservationStatus[] = [
  'declined',
  'cancelled',
  'completed',
  'no_show',
  'expired',
];

/**
 * Server-side state machine, mirrored here so the UI only offers legal
 * actions. Anything not listed is rejected.
 */
export const ALLOWED_TRANSITIONS: Record<ReservationStatus, ReservationStatus[]> = {
  pending: ['confirmed', 'declined', 'cancelled'],
  confirmed: ['seated', 'cancelled', 'no_show'],
  seated: ['completed'],
  completed: [],
  declined: [],
  cancelled: [],
  // A correction: the party turned up after all (FNF-1185).
  no_show: ['seated'],
  waitlisted: ['notified', 'seated', 'cancelled'],
  notified: ['seated', 'expired', 'cancelled', 'waitlisted'],
  expired: [],
};

export function canTransition(from: ReservationStatus, to: ReservationStatus): boolean {
  return ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * `status_reason` reservation-service writes when a request runs out its
 * window unanswered (FNF-308): a machine code, not prose. Older rows carry
 * the sentence instead, so both read as expired.
 */
export const REQUEST_EXPIRED_REASON = 'expired';
const LEGACY_REQUEST_EXPIRED_REASON = 'request expired unanswered';

/** Longest decline reason reservation-service accepts (FNF-308). */
export const DECLINE_REASON_MAX = 200;

/** Modes in which a booking can arrive as a request staff must answer. */
export const REQUEST_MODES: ReservationMode[] = ['timed', 'request', 'hybrid'];

/** A request declined by the clock rather than by staff. */
export function isExpiredRequest(r: Pick<Reservation, 'status' | 'statusReason'>): boolean {
  const reason = (r.statusReason ?? '').trim().toLowerCase();
  return (
    r.status === 'declined' &&
    (reason === REQUEST_EXPIRED_REASON || reason === LEGACY_REQUEST_EXPIRED_REASON)
  );
}

/**
 * Why a declined booking was declined, for the detail panel; null when it
 * isn't declined or staff gave no reason.
 */
export function declineReasonLabel(r: Pick<Reservation, 'status' | 'statusReason'>): string | null {
  if (r.status !== 'declined') {
    return null;
  }
  if (isExpiredRequest(r)) {
    return 'Expired — not answered in time';
  }
  const reason = r.statusReason?.trim();
  return reason ? `Declined: ${reason}` : null;
}

/**
 * Time left to answer a request, from its `expiresAt` deadline: "expires in
 * 1h 20m", "expires in 3d 2h", "expires in under a minute", or "deadline
 * passed" between the deadline and reservation-service declining it. Null
 * without a deadline (a pending row from before FNF-308).
 */
export function requestDeadlineLabel(
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!expiresAt) {
    return null;
  }
  const ms = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(ms)) {
    return null;
  }
  if (ms <= 0) {
    return 'deadline passed';
  }
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) {
    return 'expires in under a minute';
  }
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const parts =
    days > 0
      ? [`${days}d`, hours ? `${hours}h` : '']
      : hours > 0
        ? [`${hours}h`, mins ? `${mins}m` : '']
        : [`${mins}m`];
  return `expires in ${parts.filter(Boolean).join(' ')}`;
}

/**
 * Largest party staff can book or add as a walk-in from the dashboard.
 * Online guests are capped lower, by the restaurant's `party.max` setting.
 * Hardcoded for now; reservation-service accepts up to 100.
 */
export const MAX_STAFF_PARTY_SIZE = 50;

/**
 * The SMS opt-in shown under the guest phone fields (walk-in form, New/Edit
 * reservation). The Reservations Campaign's 10DLC registration (FNF-1122)
 * describes this exact wording to the carriers, so change both together.
 */
export const SMS_CONSENT = {
  text:
    'By giving a number, the guest agrees to receive ForknFind texts about this booking. ' +
    'Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
  termsUrl: LEGAL_URLS.terms,
  privacyUrl: LEGAL_URLS.privacy,
} as const;

export const DEFAULT_RESERVATION_SETTINGS: ReservationSettings = {
  mode: 'off',
  slotIntervalMinutes: 15,
  turnTimes: [
    { maxParty: 2, minutes: 75 },
    { maxParty: 4, minutes: 90 },
    { maxParty: 99, minutes: 120 },
  ],
  pacing: { maxCoversPerInterval: 24 },
  party: { min: 1, max: 8, autoConfirmUpTo: 6 },
  window: { minNoticeMinutes: 60, maxDaysAhead: 30 },
  policy: { depositCents: 0, cancelCutoffMinutes: 120, noShowGraceMinutes: 15 },
  requestExpiryMinutes: 120,
  waitlist: { quoteStrategy: 'simple', notifyTimeoutMinutes: 10 },
  channels: { google: { enabled: false }, opentable: { enabled: false, merchantId: null } },
  notifications: { sms: true, email: true, push: true, reminderMinutesBefore: [1440, 120] },
  version: 1,
};
